import { computed } from 'vue';
import { trimTextField, formattedToNumber, } from '@/utils';
import { EMAIL_PATTERN, makeRegExpPercentage, } from '@/utils/regularExpression';
import { i18n } from '@/locales';
import { validationLimitingValue } from '@/constants';
import { isRequired, greaterThanMinLength, isSameAsLimitLength, lessThanMaxAmount, isValidPattern, } from './index';
const storeDetailLc = 'views.store_management.';
const { PHONE_MIN_LENGTH, CEO_RESIDENT_REGISTRATION_NUMBER_MAX_LENGTH, CEO_DATE_OF_BIRTH_MAX_LENGTH, DELIVERY_FEE_RATE_PER_ORDER_MAX_AMOUNT, BAROMONEY_MAX_AMOUNT, DELIVERY_MANAGEMENT_FEE_RATE_PER_ORDER_MAX_AMOUNT, BUSINESS_NUMBER_MAX_LENGTH, } = validationLimitingValue;
const createStoreFormRules = (lc) => ({
    storeName: computed({
        get() {
            return [
                (v) => isRequired(trimTextField(v))
                    || i18n.t(`${lc}value_require`),
            ];
        },
        set() { },
    }),
    ceoName: computed({
        get() {
            return [
                (v) => isRequired(trimTextField(v))
                    || i18n.t(`${lc}value_require`),
            ];
        },
        set() { },
    }),
    businessCondition: computed({
        get() {
            return [
                (v) => isRequired(trimTextField(v))
                    || i18n.t(`${lc}value_require`),
            ];
        },
        set() { },
    }),
    businessType: computed({
        get() {
            return [
                (v) => isRequired(trimTextField(v))
                    || i18n.t(`${lc}value_require`),
            ];
        },
        set() { },
    }),
    currentAddress: computed({
        get() {
            return [
                (v) => isRequired(trimTextField(v))
                    || i18n.t(`${lc}value_require`),
            ];
        },
        set() { },
    }),
    phone: computed({
        get() {
            return [
                (v) => isRequired(v)
                    || i18n.t(`${lc}value_require`),
                (v) => greaterThanMinLength(v.length, PHONE_MIN_LENGTH)
                    || i18n.t(`${lc}greater_than_min_length`, {
                        length: PHONE_MIN_LENGTH,
                    }),
            ];
        },
        set() { },
    }),
    ceoPhone: computed({
        get() {
            return [
                (v) => !isRequired(v)
                    || greaterThanMinLength(v.length, PHONE_MIN_LENGTH)
                    || i18n.t(`${lc}greater_than_min_length`, {
                        length: PHONE_MIN_LENGTH,
                    }),
            ];
        },
        set() { },
    }),
    businessNumber: computed({
        get() {
            return [
                (v) => isRequired(v)
                    || i18n.t(`${lc}value_require`),
                (v) => !v
                    || isSameAsLimitLength(v.length, BUSINESS_NUMBER_MAX_LENGTH)
                    || i18n.t(`${lc}wrong_value`),
            ];
        },
        set() { },
    }),
    regNumber: computed({
        get() {
            return [
                (v) => isRequired(v)
                    || i18n.t(`${lc}value_require`),
                (v) => isSameAsLimitLength(v.length, CEO_RESIDENT_REGISTRATION_NUMBER_MAX_LENGTH)
                    || i18n.t(`${lc}wrong_length`, {
                        field: i18n.t(`${storeDetailLc}corp_registration_number`),
                        length: CEO_RESIDENT_REGISTRATION_NUMBER_MAX_LENGTH,
                    }),
            ];
        },
        set() { },
    }),
    birth: computed({
        get() {
            return [
                (v) => isRequired(v)
                    || i18n.t(`${lc}value_require`),
                (v) => isSameAsLimitLength(v.length, CEO_DATE_OF_BIRTH_MAX_LENGTH)
                    || i18n.t(`${lc}wrong_length`, {
                        field: i18n.t(`${storeDetailLc}date_of_birth`),
                        length: CEO_DATE_OF_BIRTH_MAX_LENGTH,
                    }),
            ];
        },
        set() { },
    }),
    // 건당 관리비 정률
    deliveryManagementFeeRatePerOrder: computed({
        get() {
            return [
                (v) => !v
                    || lessThanMaxAmount((v), DELIVERY_MANAGEMENT_FEE_RATE_PER_ORDER_MAX_AMOUNT)
                    || i18n.t(`${lc}available_max_amount_to_enter`, {
                        max: '99.9',
                    }),
                // 패턴 채크 or 소수점 채크
                (v) => {
                    const regExp = makeRegExpPercentage(1);
                    if (regExp.test(v)) {
                        return true;
                    }
                    return i18n.t(`${lc}max_decimal_place`, [
                        '1',
                    ]);
                },
            ];
        },
        set() { },
    }),
    // 예치금
    depositBaromoney: computed({
        get() {
            return [
                (v) => !v
                    || lessThanMaxAmount((formattedToNumber(v)), BAROMONEY_MAX_AMOUNT)
                    || i18n.t(`${lc}wrong_value`),
            ];
        },
        set() { },
    }),
    // 대표이메일
    ceoEmail: computed({
        get() {
            return [
                (v) => isRequired(v)
                    || i18n.t(`${lc}value_require`),
                (v) => isValidPattern(EMAIL_PATTERN, v)
                    || i18n.t(`${lc}email_type`),
            ];
        },
        set() { },
    }),
    // 좌표
    coordinate: computed({
        get() {
            return [
                (v) => isRequired(v)
                    || i18n.t(`${lc}value_require`),
            ];
        },
        set() { },
    }),
    // 건당 상점 수수료 정률
    deliveryFeeRatePerOrder: computed({
        get() {
            return [
                // 최대값 채크
                (v) => !v
                    || lessThanMaxAmount((v), DELIVERY_FEE_RATE_PER_ORDER_MAX_AMOUNT)
                    || i18n.t(`${lc}available_max_amount_to_enter`, {
                        max: '99.9',
                    }),
                // 패턴 채크 or 소수점 채크
                (v) => {
                    const regExp = makeRegExpPercentage(1);
                    if (regExp.test(v)) {
                        return true;
                    }
                    return i18n.t(`${lc}max_decimal_place`, [
                        '1',
                    ]);
                },
            ];
        },
        set() { },
    }),
    // 본사 계약 설정
    adjustmentType: computed({
        get() {
            return [
                (v) => isRequired(v)
                    || i18n.t(`${lc}value_require`),
            ];
        },
        set() { },
    }),
});
export default createStoreFormRules;
